import styled, { createGlobalStyle, css } from "styled-components";
import { nanoid } from "nanoid";
import { textStyles } from "./textStyles.js";
import { baseStyles } from "./BaseStyles.js";
import { token } from "./token.js";
const prefix = `b-modal-${nanoid()}`;
const classNames = {
  root: prefix,
  overlay: `${prefix}__overlay`,
  modalContainer: `${prefix}__container`,
  modal: `${prefix}__modal`,
  modalSmall: `${prefix}__modal--small`,
  modalMedium: `${prefix}__modal--medium`,
  modalFullScreen: `${prefix}__modal--full-screen`,
  modalHasSidebar: `${prefix}__modal--has-sidebar`,
  overlayAnimationIn: `${prefix}-overlay-in`,
  overlayAnimationOut: `${prefix}-overlay-out`,
  modalAnimationIn: `${prefix}-container-in`,
  modalAnimationOut: `${prefix}-container-out`
};
const GlobalStyles = createGlobalStyle((props) => css(["@keyframes ", "{0%{opacity:0;}100%{opacity:1;}}@keyframes ", "{0%{opacity:1;}100%{opacity:0;}}@keyframes ", "{0%{opacity:0.5;transform:scale(0.95);}100%{opacity:1;transform:scale(1);}}@keyframes ", "{0%{opacity:1;transform:scale(1);}100%{opacity:0;transform:scale(0.95);}}.", ",.", ",.", "{animation-fill-mode:forwards !important;}.", ",.", "{position:fixed;top:0;right:0;bottom:0;left:0;z-index:", ";}.", "{background:", ";}.", "{display:flex;justify-content:center;align-items:center;padding:", ";}.", "{", " position:relative;display:grid;grid-template-areas:'sidebar header' 'sidebar content' 'sidebar footer';grid-template-rows:auto 1fr auto;grid-template-columns:auto var(--ds-modal-content-width);box-sizing:border-box;border-radius:", ";background:", ";box-shadow:", ";}.", "{--ds-modal-content-width:520px;max-height:100%;}.", ".", "{max-height:min(520px,100%);}.", "{--ds-modal-content-width:minmax(auto,866px);max-height:min(752px,100%);}.", "{--ds-modal-content-width:minmax( 0,1fr );width:100%;height:100%;min-height:0;}"], classNames.overlayAnimationIn, classNames.overlayAnimationOut, classNames.modalAnimationIn, classNames.modalAnimationOut, classNames.overlay, classNames.modalContainer, classNames.modal, classNames.overlay, classNames.modalContainer, props.zIndex, classNames.overlay, token.gray200a, classNames.modalContainer, token.spacing6, classNames.modal, baseStyles, token.radiusLarge, token.gray10, token.elevation8, classNames.modalSmall, classNames.modalSmall, classNames.modalHasSidebar, classNames.modalMedium, classNames.modalFullScreen));
const HeaderStyled = styled.div.withConfig({
  displayName: "HeaderStyled",
  componentId: "sc-13hyd7w-0"
})(["display:flex;grid-area:header;gap:", ";align-items:center;box-sizing:border-box;height:72px;padding:", " ", ";", ""], token.spacing4, token.spacing4, token.spacing6, (props) => !props.$hideCloseIcon && css(["padding-right:calc( 50px + ", " + ", " );"], token.spacing5, token.spacing4));
const AdditionalActions = styled.div.withConfig({
  displayName: "AdditionalActions",
  componentId: "sc-13hyd7w-1"
})(["display:flex;gap:", ";"], token.spacing3);
const BackButton = styled.div.withConfig({
  displayName: "BackButton",
  componentId: "sc-13hyd7w-2"
})(["margin-left:calc(", " * -1);"], token.spacing3);
const Headings = styled.div.withConfig({
  displayName: "Headings",
  componentId: "sc-13hyd7w-3"
})(["flex:1;min-width:0;"]);
const Title = styled.h2.withConfig({
  displayName: "Title",
  componentId: "sc-13hyd7w-4"
})(["", " overflow:hidden;margin:0;color:", ";text-overflow:ellipsis;white-space:nowrap;"], textStyles.uiTextL, token.colorText);
const SecondaryInformation = styled.h3.withConfig({
  displayName: "SecondaryInformation",
  componentId: "sc-13hyd7w-5"
})(["", " overflow:hidden;margin:0;color:", ";text-overflow:ellipsis;white-space:nowrap;"], textStyles.uiTextS, token.colorTextMuted);
const ContentStyled = styled.div.withConfig({
  displayName: "ContentStyled",
  componentId: "sc-13hyd7w-6"
})(["position:relative;grid-area:content;min-height:0;&:nth-last-child(2){--ds-modal-content-bottom-radius:", ";}&::before,&::after{content:'';position:absolute;left:0;display:block;width:100%;height:4px;}&::before{top:0;z-index:1;background:linear-gradient(to bottom,", ",transparent);box-shadow:inset 0 1px 0 0 ", ";}&::after{bottom:0;border-bottom-right-radius:var(--ds-modal-content-bottom-radius);border-bottom-left-radius:var(--ds-modal-content-bottom-radius);background:linear-gradient(to top,", ",transparent);box-shadow:inset 0 -1px 0 0 ", ";}"], token.radiusLarge, token.gray50a, token.gray100a, token.gray50a, token.gray100a);
const ContentInner = styled.div.withConfig({
  displayName: "ContentInner",
  componentId: "sc-13hyd7w-7"
})(["", " position:relative;overflow-x:hidden;overflow-y:auto;box-sizing:border-box;height:100%;padding:", " ", ";color:", ";font-family:", ";scroll-behavior:smooth;&::before,&::after{content:'';position:relative;left:0;z-index:1;display:block;height:", ";}&::before{margin:calc(", " * -1) calc(", " * -1) 0;background:linear-gradient( to bottom,", ",", " 50%,rgb(255 255 255 / 0) );}&::after{margin:0 calc(", " * -1) calc(", " * -1);border-bottom-right-radius:var(--ds-modal-content-bottom-radius);border-bottom-left-radius:var(--ds-modal-content-bottom-radius);background:linear-gradient( to top,", ",", " 50%,rgb(255 255 255 / 0) );}"], textStyles.paragraphM, token.spacing5, token.spacing6, token.colorText, token.brandFontStackBase, token.spacing5, token.spacing5, token.spacing6, token.gray10, token.gray10, token.spacing6, token.spacing5, token.gray10, token.gray10);
const ContentExpander = styled.div.withConfig({
  displayName: "ContentExpander",
  componentId: "sc-13hyd7w-8"
})(["min-height:100%;"]);
const FooterStyled = styled.div.withConfig({
  displayName: "FooterStyled",
  componentId: "sc-13hyd7w-9"
})(["display:flex;grid-area:footer;padding:", ";"], token.spacing6);
const ActionsRight = styled.div.withConfig({
  displayName: "ActionsRight",
  componentId: "sc-13hyd7w-10"
})(["margin-left:auto;"]);
const CloseButton = styled.div.withConfig({
  displayName: "CloseButton",
  componentId: "sc-13hyd7w-11"
})(["position:absolute;top:", ";right:", ";"], token.spacing5, token.spacing5);
const SidebarStyled = styled.div.withConfig({
  displayName: "SidebarStyled",
  componentId: "sc-13hyd7w-12"
})(["grid-area:sidebar;"]);
export {
  ActionsRight,
  AdditionalActions,
  BackButton,
  CloseButton,
  ContentExpander,
  ContentInner,
  ContentStyled,
  FooterStyled,
  GlobalStyles,
  HeaderStyled,
  Headings,
  SecondaryInformation,
  SidebarStyled,
  Title,
  classNames
};
//# sourceMappingURL=ModalBase.styles.js.map
